import { useState, useRef } from 'react';
import { API_ROUTES } from '@/lib/api-routes';
import Cropper from 'react-easy-crop';
import moment from 'moment-timezone';
import { ErrorBanner } from './ErrorBanner';

interface ProfileSetupModalProps {
  email: string;
  defaultFirstName: string;
  defaultLastName: string;
  onComplete: () => void;
}

export function ProfileSetupModal({ email, defaultFirstName, defaultLastName, onComplete }: ProfileSetupModalProps) {
  const [firstName, setFirstName] = useState(defaultFirstName);
  const [lastName, setLastName] = useState(defaultLastName);
  const [displayName, setDisplayName] = useState(`${defaultFirstName} ${defaultLastName}`.trim());
   const [profilePicture, setProfilePicture] = useState('');
   const [uploading, setUploading] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);
    // Time zone list
    const timeZoneList = moment.tz.names();
    // Image cropping helpers
    const onCropComplete = (_, croppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPixels);
    };

    const showFileDialog = () => {
      if (fileInputRef.current) {fileInputRef.current.click();}
    };

    const handleFileChange = async (e) => {
      const file = e.target.files[0];
      if (!file) {return;}
       const reader = new window.FileReader();
      reader.onload = () => {
         const result = reader.result;
         if (typeof result === 'string') {
           setProfilePicture(result);
           setShowCrop(true);
         } else {
           setError('Failed to read image');
         }
      };
      reader.readAsDataURL(file);
    };

    // Utility to crop and resize image
    async function getCroppedImg(imageSrc, cropPixels, size = 256) {
      const image = new window.Image();
      image.src = imageSrc;
      await new Promise((resolve) => { image.onload = resolve; });
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.arc(size/2, size/2, size/2, 0, 2 * Math.PI);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(
        image,
        cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
        0, 0, size, size
      );
      return canvas.toDataURL('image/png');
    }

    const handleCropDone = async () => {
      if (!croppedAreaPixels) {return;}
      const cropped = await getCroppedImg(profilePicture, croppedAreaPixels);
       setShowCrop(false);
      // Upload to backend
      setUploading(true);
      try {
        const blob = await (await fetch(cropped)).blob();
        const formData = new window.FormData();
        formData.append('file', blob, 'profile.png');
        const res = await fetch(API_ROUTES.profile.uploadPicture(), {
          method: 'POST',
          body: formData
        });
        const data = (await res.json()) as { ok?: boolean; url?: string; error?: string };
        if (data.ok && data.url) {
          setProfilePicture(data.url);
        } else {
          setProfilePicture('');
          setError(data.error || 'Failed to upload profile picture');
        }
      } catch {
        setError('Failed to upload profile picture');
      } finally {
        setUploading(false);
      }
    };

    const handleRemovePicture = () => {
      setProfilePicture('');
    };
  const [phoneNumber, setPhoneNumber] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const res = await fetch(API_ROUTES.profile.update(), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: firstName,
          lastName: lastName,
          displayName: displayName || `${firstName} ${lastName}`,
          profilePicture: profilePicture || undefined,
          phoneNumber: phoneNumber || undefined,
          timeZone: timeZone || undefined,
          jobTitle: jobTitle || undefined,
          department: department || undefined,
          bio: bio || undefined
        })
      });
      if (!res.ok) {
        const errorData: { error?: string } = await res.json().catch(() => ({}) as { error?: string });
        throw new Error(errorData.error || 'Failed to update profile');
      }
      onComplete();
    } catch (err) {
      setError(String(err));
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-5 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">Welcome! Complete Your Profile</h2>
          <p className="mt-2 text-sm text-slate-600">
            This is your first time logging in. Please verify and complete your profile information.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

          <div>
            <div className="block text-sm font-medium text-slate-700 mb-2">
              Email <span className="text-slate-500">(verified)</span>
            </div>
            <p className="text-slate-900 px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">{email}</p>
          </div>

          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-2">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="First name"
              required
            />
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 mb-2">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Last name"
              required
            />
          </div>

          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-slate-700 mb-2">
              Display Name <span className="text-slate-500">(optional)</span>
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={`${firstName} ${lastName}`}
            />
            <p className="mt-1 text-xs text-slate-500">
              How your name will appear throughout the application
            </p>
          </div>

          <div className="pt-4 border-t border-slate-200">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Additional Information (Optional)</h3>
            <p className="text-xs text-slate-500 mb-4">
              This information helps personalize your experience and can be updated anytime.
            </p>
            
            <div className="space-y-4">
              <div>
                <div className="block text-sm font-medium text-slate-700 mb-2">Profile Picture</div>
                <div className="flex items-center gap-4">
                  {profilePicture ? (
                    <img src={profilePicture} alt="Profile preview" className="w-16 h-16 rounded-full object-cover border-2 border-slate-200" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white text-xl font-semibold border-2 border-slate-200">
                      {firstName?.[0] || ''}{lastName?.[0] || ''}
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <button type="button" className="btn btn-sm btn-outline" onClick={showFileDialog}>
                    Upload
                  </button>
                  {profilePicture && (
                    <button type="button" className="btn btn-sm btn-outline text-red-600" onClick={handleRemovePicture}>
                      Remove
                    </button>
                  )}
                </div>
                {showCrop && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
                    <div className="bg-white rounded-lg p-6 shadow-lg w-[350px]">
                      <h4 className="mb-2 font-semibold">Crop Profile Picture</h4>
                      <div className="relative w-64 h-64 bg-gray-100">
                        <Cropper
                          image={profilePicture}
                          crop={crop}
                          zoom={zoom}
                          aspect={1}
                          cropShape="round"
                          showGrid={false}
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onCropComplete={onCropComplete}
                        />
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button type="button" className="btn btn-primary" onClick={handleCropDone} disabled={uploading}>{uploading ? 'Uploading...' : 'Done'}</button>
                        <button type="button" className="btn btn-outline" onClick={() => setShowCrop(false)} disabled={uploading}>Cancel</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    id="phoneNumber"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="timeZone" className="block text-sm font-medium text-slate-700 mb-2">
                    Time Zone
                  </label>
                  <select
                    id="timeZone"
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a time zone</option>
                    {timeZoneList.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-slate-700 mb-2">
                    Job Title
                  </label>
                  <input
                    id="jobTitle"
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Accountant, Owner, etc."
                  />
                </div>

                <div>
                  <label htmlFor="department" className="block text-sm font-medium text-slate-700 mb-2">
                    Department
                  </label>
                  <input
                    id="department"
                    type="text"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Finance, Operations, etc."
                  />
                </div>
              </div>

              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-slate-700 mb-2">
                  Bio
                </label>
                <textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="A brief description about yourself..."
                />
                <p className="mt-1 text-xs text-slate-500">
                  {bio.length}/500 characters
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Complete Setup'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
